/**
 * Management dashboard — US-A10 / FR-ADM-12.
 *
 * This is intentionally a decision dashboard, not a gallery of vanity
 * numbers: every metric either answers a growth question or points to a next
 * operational action. Attribution metrics remain visibly unavailable until the
 * mobile analytics events in US-X06 are collected.
 */
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  ReloadOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Segmented,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Link } from 'react-router'
import { space } from '@horizon/tokens'
import { formatInteger, formatPrice } from '@horizon/i18n'
import { supabaseClient } from '@/lib/supabase'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { Locale } from '@/types'

type Period = '7d' | '30d' | 'custom'

interface DashboardMetrics {
  total_students: number
  active_subscribers: number
  new_signups: number
  previous_signups: number
  recorded_revenue_mru: number
  previous_revenue_mru: number
  watch_hours: number
  previous_watch_hours: number
  new_paid_subscribers: number
  expiring_subscribers: number
  expired_subscriptions: number
  pending_transactions: number
  videos_processing: number
}

interface DashboardTrendPoint {
  day: string
  signups: number
  revenue_mru: number
  watch_hours: number
}

interface TopContent {
  content_id: string
  title_ar: string
  title_fr: string
  subject_name_fr: string
  watch_hours: number
  learners: number
}

const number = (value: number) => formatInteger(value)
const decimal = (value: number, locale: Locale) =>
  new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'fr-FR', { maximumFractionDigits: 1 }).format(
    value,
  )

const asNumber = (value: number | string | null | undefined) => Number(value ?? 0)

const selectedRange = (period: Period, customRange: [Dayjs, Dayjs] | null) => {
  if (period === 'custom' && customRange) {
    return {
      from: customRange[0].startOf('day'),
      to: customRange[1].endOf('day').add(1, 'millisecond'),
    }
  }
  const days = period === '7d' ? 7 : 30
  return {
    from: dayjs()
      .subtract(days - 1, 'day')
      .startOf('day'),
    to: dayjs().endOf('day').add(1, 'millisecond'),
  }
}

const displayPeriod = (from: Dayjs, to: Dayjs) =>
  `${from.format('DD MMM')} — ${to.subtract(1, 'millisecond').format('DD MMM YYYY')}`

const percentageDelta = (current: number, previous: number) => {
  if (previous <= 0) return null
  return ((current - previous) / previous) * 100
}

const Trend = ({ current, previous }: { current: number; previous: number }) => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const delta = percentageDelta(current, previous)
  if (delta === null)
    return (
      <Typography.Text type="secondary">
        {translate('admin.screens.dashboard.noComparison')}
      </Typography.Text>
    )
  const positive = delta >= 0
  return (
    <Typography.Text className={positive ? 'dashboard-trend-positive' : 'dashboard-trend-negative'}>
      {positive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{' '}
      {translate('admin.screens.dashboard.trendDelta', {
        value: decimal(Math.abs(delta), locale),
      })}
    </Typography.Text>
  )
}

const MetricCard = ({
  label,
  value,
  suffix,
  icon,
  current,
  previous,
  loading,
}: {
  label: string
  value: string
  suffix?: string
  icon: React.ReactNode
  current?: number
  previous?: number
  loading: boolean
}) => {
  const translate = useAdminTranslate()

  return (
    <Card className="dashboard-metric-card">
      {loading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '55%' }} />
      ) : (
        <>
          <div className="dashboard-metric-heading">
            <Typography.Text>{label}</Typography.Text>
            <span className="dashboard-metric-icon">{icon}</span>
          </div>
          <Statistic value={value} suffix={suffix} />
          {typeof current === 'number' && typeof previous === 'number' ? (
            <Trend current={current} previous={previous} />
          ) : (
            <Typography.Text type="secondary">
              {translate('admin.screens.dashboard.currentView')}
            </Typography.Text>
          )}
        </>
      )}
    </Card>
  )
}

const ChartCard = ({
  title,
  description,
  data,
  dataKey,
  formatter,
}: {
  title: string
  description: string
  data: DashboardTrendPoint[]
  dataKey: 'signups' | 'watch_hours'
  formatter: (value: number) => string
}) => {
  const translate = useAdminTranslate()

  return (
    <Card
      className="dashboard-chart-card"
      title={title}
      extra={<Typography.Text type="secondary">{description}</Typography.Text>}
    >
      {data.some(point => point[dataKey] > 0) ? (
        <div className="dashboard-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, bottom: 0 }}>
              <defs>
                <linearGradient id={`dashboard-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--admin-primary)" stopOpacity={0.24} />
                  <stop offset="95%" stopColor="var(--admin-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--admin-border)" strokeDasharray="3 3" />
              <XAxis
                axisLine={false}
                dataKey="day"
                tickFormatter={value => dayjs(value).format('DD/MM')}
                tickLine={false}
                tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickFormatter={value => formatter(Number(value))}
                tickLine={false}
                tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
              />
              <RechartsTooltip
                formatter={(value: number) => formatter(Number(value))}
                labelFormatter={label => dayjs(label).format('dddd D MMMM')}
                contentStyle={{ borderColor: 'var(--admin-border)', borderRadius: 12 }}
              />
              <Area
                type="monotone"
                dataKey={dataKey}
                name={
                  dataKey === 'signups'
                    ? translate('admin.screens.dashboard.signupsSeries')
                    : translate('admin.screens.dashboard.watchHoursSeries')
                }
                stroke="var(--admin-primary)"
                strokeWidth={2.5}
                fill={`url(#dashboard-${dataKey})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translate('admin.screens.dashboard.noActivity')}
        />
      )}
    </Card>
  )
}

export const DashboardPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const [period, setPeriod] = useState<Period>('30d')
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null)
  const range = selectedRange(period, customRange)
  const rangeLabel = displayPeriod(range.from, range.to)

  const dashboardQuery = useQuery({
    queryKey: ['admin-dashboard', range.from.toISOString(), range.to.toISOString()],
    queryFn: async () => {
      const params = { p_from: range.from.toISOString(), p_to: range.to.toISOString() }
      const [metricsResult, trendResult, contentResult] = await Promise.all([
        supabaseClient.rpc('admin_dashboard_metrics', params),
        supabaseClient.rpc('admin_dashboard_trend', params),
        supabaseClient.rpc('admin_dashboard_top_content', { ...params, p_limit: 10 }),
      ])
      const error = metricsResult.error ?? trendResult.error ?? contentResult.error
      if (error) throw error
      return {
        metrics: ((metricsResult.data ?? [])[0] ?? null) as DashboardMetrics | null,
        trend: (trendResult.data ?? []) as DashboardTrendPoint[],
        content: (contentResult.data ?? []) as TopContent[],
      }
    },
  })

  const metrics = dashboardQuery.data?.metrics
  const trend = dashboardQuery.data?.trend ?? []
  const topContent = dashboardQuery.data?.content ?? []
  const loading = dashboardQuery.isLoading
  const activeCoverage =
    metrics && metrics.total_students > 0
      ? (metrics.active_subscribers / metrics.total_students) * 100
      : 0

  const priorities = metrics
    ? [
        metrics.expiring_subscribers > 0
          ? {
              tone: 'warning' as const,
              icon: <SafetyCertificateOutlined />,
              title: translate('admin.screens.dashboard.expiringTitle', {
                count: number(metrics.expiring_subscribers),
              }),
              description: translate('admin.screens.dashboard.expiringBody'),
              to: '/users?subscription_status=active',
              action: translate('admin.screens.dashboard.expiringAction'),
            }
          : null,
        metrics.pending_transactions > 0
          ? {
              tone: 'info' as const,
              icon: <CreditCardOutlined />,
              title: translate('admin.screens.dashboard.pendingTitle', {
                count: number(metrics.pending_transactions),
              }),
              description: translate('admin.screens.dashboard.pendingBody'),
              to: '/transactions?status=pending',
              action: translate('admin.screens.dashboard.pendingAction'),
            }
          : null,
        metrics.videos_processing > 0
          ? {
              tone: 'info' as const,
              icon: <VideoCameraOutlined />,
              title: translate('admin.screens.dashboard.mediaTitle', {
                count: number(metrics.videos_processing),
              }),
              description: translate('admin.screens.dashboard.mediaBody'),
              to: '/ingest',
              action: translate('admin.screens.dashboard.mediaAction'),
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => Boolean(item))
    : []

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <section className="admin-page-heading dashboard-heading">
        <div>
          <Typography.Text className="admin-kicker">
            {translate('admin.screens.dashboard.kicker')}
          </Typography.Text>
          <Typography.Title level={1} className="admin-page-title">
            {translate('dashboard.dashboard')}
          </Typography.Title>
          <Typography.Text className="admin-page-subtitle">
            {translate('admin.screens.dashboard.subtitle')}
          </Typography.Text>
        </div>
        <Space wrap>
          <Tag className="dashboard-period-tag">{rangeLabel}</Tag>
          <Button
            icon={<ReloadOutlined />}
            loading={dashboardQuery.isFetching}
            onClick={() => void dashboardQuery.refetch()}
          >
            {translate('admin.screens.dashboard.refresh')}
          </Button>
        </Space>
      </section>

      <Card className="dashboard-control-card">
        <Space wrap size="middle">
          <Segmented<Period>
            value={period}
            onChange={setPeriod}
            options={[
              { value: '7d', label: translate('admin.screens.dashboard.period7') },
              { value: '30d', label: translate('admin.screens.dashboard.period30') },
              { value: 'custom', label: translate('admin.screens.dashboard.periodCustom') },
            ]}
          />
          {period === 'custom' ? (
            <DatePicker.RangePicker
              allowClear={false}
              format="DD/MM/YYYY"
              value={customRange}
              onChange={value =>
                setCustomRange(value?.[0] && value?.[1] ? [value[0], value[1]] : null)
              }
            />
          ) : null}
          <Typography.Text type="secondary">
            {translate('admin.screens.dashboard.comparisonNote')}
          </Typography.Text>
        </Space>
      </Card>

      {dashboardQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={translate('admin.screens.dashboard.errorTitle')}
          description={translate('admin.screens.dashboard.errorBody')}
          action={
            <Button size="small" onClick={() => void dashboardQuery.refetch()}>
              {translate('admin.screens.dashboard.retry')}
            </Button>
          }
        />
      ) : null}

      <section>
        <div className="dashboard-section-heading">
          <div>
            <Typography.Title level={3}>
              {translate('admin.screens.dashboard.commercialTitle')}
            </Typography.Title>
            <Typography.Text type="secondary">
              {translate('admin.screens.dashboard.commercialSubtitle')}
            </Typography.Text>
          </div>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              label={translate('admin.screens.dashboard.totalStudents')}
              value={number(asNumber(metrics?.total_students))}
              icon={<TeamOutlined />}
              loading={loading}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              label={translate('admin.screens.dashboard.activeSubscribers')}
              value={number(asNumber(metrics?.active_subscribers))}
              icon={<CheckCircleOutlined />}
              loading={loading}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              label={translate('admin.screens.dashboard.newSignups')}
              value={number(asNumber(metrics?.new_signups))}
              icon={<RiseOutlined />}
              current={asNumber(metrics?.new_signups)}
              previous={asNumber(metrics?.previous_signups)}
              loading={loading}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              label={translate('admin.screens.dashboard.recordedRevenue')}
              value={formatPrice(asNumber(metrics?.recorded_revenue_mru), locale)}
              icon={<BankOutlined />}
              current={asNumber(metrics?.recorded_revenue_mru)}
              previous={asNumber(metrics?.previous_revenue_mru)}
              loading={loading}
            />
          </Col>
        </Row>
      </section>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ChartCard
            title={translate('admin.screens.dashboard.signupsChart')}
            description={translate('admin.screens.dashboard.acquisition')}
            data={trend}
            dataKey="signups"
            formatter={value => number(value)}
          />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard
            title={translate('admin.screens.dashboard.studyTime')}
            description={translate('admin.screens.dashboard.engagement')}
            data={trend}
            dataKey="watch_hours"
            formatter={value =>
              translate('admin.screens.dashboard.hoursValue', { value: decimal(value, locale) })
            }
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card
            className="dashboard-content-card"
            title={translate('admin.screens.dashboard.topContentTitle')}
            extra={
              <Typography.Text type="secondary">
                {translate('admin.screens.dashboard.watchHoursSeries')}
              </Typography.Text>
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : topContent.length ? (
              <div className="dashboard-ranking">
                {topContent.map((item, index) => (
                  <div className="dashboard-ranking-row" key={item.content_id}>
                    <span className="dashboard-ranking-position">{index + 1}</span>
                    <div className="dashboard-ranking-title">
                      <Link to={`/content-items/edit/${item.content_id}`}>
                        {locale === 'ar' ? item.title_ar || item.title_fr : item.title_fr}
                      </Link>
                      <Typography.Text type="secondary">{item.subject_name_fr}</Typography.Text>
                    </div>
                    <Typography.Text strong>
                      {translate('admin.screens.dashboard.hoursValue', {
                        value: decimal(asNumber(item.watch_hours), locale),
                      })}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {translate('admin.screens.dashboard.learners', {
                        count: number(asNumber(item.learners)),
                      })}
                    </Typography.Text>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={translate('admin.screens.dashboard.noWatch')}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card className="dashboard-health-card">
            <Typography.Text className="admin-kicker">
              {translate('admin.screens.dashboard.coverageKicker')}
            </Typography.Text>
            <Statistic value={activeCoverage} precision={1} suffix="%" />
            <Typography.Paragraph type="secondary" style={{ marginBottom: space[3] }}>
              {translate('admin.screens.dashboard.coverageBody')}
            </Typography.Paragraph>
            <div className="dashboard-health-divider" />
            <Space direction="vertical" size={space[2]} style={{ width: '100%' }}>
              <span>
                <strong>{number(asNumber(metrics?.new_paid_subscribers))}</strong>{' '}
                {translate('admin.screens.dashboard.paidActivations')}
              </span>
              <span>
                <strong>{number(asNumber(metrics?.expired_subscriptions))}</strong>{' '}
                {translate('admin.screens.dashboard.expiredSubscriptions')}
              </span>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            className="dashboard-priority-card"
            title={translate('admin.screens.dashboard.prioritiesTitle')}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : priorities.length ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {priorities.map(priority => (
                  <Alert
                    key={priority.title}
                    type={priority.tone}
                    showIcon
                    icon={priority.icon}
                    message={priority.title}
                    description={priority.description}
                    action={
                      <Link to={priority.to}>
                        {priority.action} <ArrowRightOutlined />
                      </Link>
                    }
                  />
                ))}
              </Space>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={translate('admin.screens.dashboard.noPriorities')}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            className="dashboard-insight-card"
            title={translate('admin.screens.dashboard.nextTitle')}
          >
            <Typography.Paragraph type="secondary">
              {translate('admin.screens.dashboard.nextIntro')}
            </Typography.Paragraph>
            <ul className="dashboard-insight-list">
              <li>
                <strong>{translate('admin.screens.dashboard.nextAttributionLabel')}</strong>{' '}
                {translate('admin.screens.dashboard.nextAttribution')}
              </li>
              <li>
                <strong>{translate('admin.screens.dashboard.nextActivationLabel')}</strong>{' '}
                {translate('admin.screens.dashboard.nextActivation')}
              </li>
              <li>
                <strong>{translate('admin.screens.dashboard.nextConversionLabel')}</strong>{' '}
                {translate('admin.screens.dashboard.nextConversion')}
              </li>
              <li>
                <strong>{translate('admin.screens.dashboard.nextRetentionLabel')}</strong>{' '}
                {translate('admin.screens.dashboard.nextRetention')}
              </li>
            </ul>
            <Typography.Text type="secondary">
              {translate('admin.screens.dashboard.nextFootnote')}
            </Typography.Text>
          </Card>
        </Col>
      </Row>
    </Space>
  )
}
